import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ADMIN_ROLES, IMAGE_BASE } from "../../utils/constants";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Box,
  InputLabel,
  FormControl,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import errorHandler from "../../utils/errorHandler";
import TipTapEditor from "../../components/editor/TipTapEditor";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { sportNewsApi, sportNewsCategoryApi } from "../../services/AllServices";

const editorContentValidation = (cmsData) => {
  if (cmsData === undefined) {
    return false;
  }
  const plainText = cmsData.replace(/<[^>]+>/g, "").trim();
  if (!plainText) {
    return false;
  }
  return true;
};

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Title is required")
    .min(2, "Please enter at least 2 characters")
    .max(200, "Title must not be more than 200 characters."),
  shortDescription: Yup.string()
    .required("Summary is required")
    .test(
      "max-30-words",
      "Summary must not be more than 30 words.",
      (value) => {
        if (!value) return true;
        const wordCount = value.trim().split(/\s+/).filter(Boolean).length;
        return wordCount <= 30;
      }
    ),
  description: Yup.string().test(
    "required-description",
    "Article is required",
    (value) => editorContentValidation(value)
  ),
  categoryId: Yup.string().required("Category is required"),
  author: Yup.string().required("Author is required"),
  status: Yup.mixed().required("Status is required"),
});

export default function AddEditSportNews() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isReadOnly = user?.role === ADMIN_ROLES.MGT || user?.role === ADMIN_ROLES.JUNIOR;
  const { id } = useParams();

  const [imagePreview, setImagePreview] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryError, setNewCategoryError] = useState("");
  const [addingCategory, setAddingCategory] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      shortDescription: "",
      description: "",
      categoryId: "",
      author: "",
      tags: "",
      featured: false,
      status: 1,
    },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("shortDescription", values.shortDescription);
      formData.append("description", values.description);
      formData.append("categoryId", values.categoryId);
      formData.append("author", values.author);
      formData.append("status", values.status);
      formData.append("featured", values.featured);
      formData.append("imagePath", "news");

      // Convert comma-separated string to JSON array string
      if (values.tags) {
        const tagsArray = values.tags.split(",").map(tag => tag.trim()).filter(t => t);
        formData.append("tags", JSON.stringify(tagsArray));
      } else {
        formData.append("tags", JSON.stringify([]));
      }

      if (selectedImage) {
        formData.append("image", selectedImage);
      } else if (!imagePreview && id) {
        formData.append("removeImage", "true");
      }

      const apiCall = id
        ? sportNewsApi.updateSportNews(formData, id)
        : sportNewsApi.addSportNews(formData);

      apiCall
        .then((res) => {
          toast.success(res?.data?.message);
          navigate("/sport-news");
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setValues } = formik;

  useEffect(() => {
    sportNewsCategoryApi
      .getAllSportNewsCategories({
        status: true,
        pageNumber: 1,
        pageSize: 100,
      })
      .then((res) => {
        setCategories(res?.data?.data?.result || []);
      })
      .catch((err) => {
        errorHandler(err);
      });
  }, []);

  useEffect(() => {
    if (id) {
      sportNewsApi
        .getOneSportNews(id)
        .then(async (res) => {
          const data = res?.data?.data;
          setValues({
            title: data.title || "",
            shortDescription: data.shortDescription || "",
            description: data.description || "",
            categoryId: data.categoryId || "",
            author: data.author || "",
            tags: data.tags ? data.tags.join(", ") : "",
            featured: data.featured || false,
            status: data.status !== undefined ? data.status : 1,
          });

          if (data.image) {
            setImagePreview(`${IMAGE_BASE}${data.image.replace(/\\/g, "/")}`);
          }

          // Ensure current category appears in dropdown even if inactive
          if (data.categoryId) {
            try {
              const catRes = await sportNewsCategoryApi.getOneSportNewsCategory(
                data.categoryId
              );
              const cat = catRes?.data?.data;
              if (cat) {
                setCategories((prev) =>
                  prev.some((c) => c._id === cat._id) ? prev : [...prev, cat]
                );
              }
            } catch {
              // ignore — dropdown may just omit missing category
            }
          }
        })
        .catch((err) => {
          errorHandler(err);
        });
    }
  }, [id, setValues]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview("");
  };

  const openAddCategoryModal = () => {
    setNewCategoryName("");
    setNewCategoryError("");
    setAddCategoryOpen(true);
  };

  const closeAddCategoryModal = () => {
    if (addingCategory) return;
    setAddCategoryOpen(false);
    setNewCategoryName("");
    setNewCategoryError("");
  };

  const validateNewCategoryName = (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return "Category name is required";
    if (trimmed.length < 2) return "Please enter at least 2 characters";
    if (trimmed.length > 100) return "Name must not be more than 100 characters.";
    return "";
  };

  const handleAddCategory = async () => {
    const error = validateNewCategoryName(newCategoryName);
    if (error) {
      setNewCategoryError(error);
      return;
    }

    setAddingCategory(true);
    try {
      const res = await sportNewsCategoryApi.addSportNewsCategory({
        name: newCategoryName.trim(),
        status: true,
      });
      const created = res?.data?.data;
      toast.success(res?.data?.message || "Category added successfully");
      if (created) {
        setCategories((prev) =>
          prev.some((c) => c._id === created._id) ? prev : [...prev, created]
        );
        setFieldValue("categoryId", created._id);
      }
      setAddCategoryOpen(false);
      setNewCategoryName("");
      setNewCategoryError("");
    } catch (err) {
      errorHandler(err);
    } finally {
      setAddingCategory(false);
    }
  };

  const handleDeleteCategory = (cat, event) => {
    event.preventDefault();
    event.stopPropagation();

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await sportNewsCategoryApi.deleteSportNewsCategory(cat._id);
        if (res?.status === 200) {
          toast.success(res?.data?.message || "Deleted successfully", {
            toastId: "category-delete-success",
          });
          setCategories((prev) => prev.filter((c) => c._id !== cat._id));
          if (formik.values.categoryId === cat._id) {
            setFieldValue("categoryId", "");
          }
        } else {
          toast.error(res?.data?.message || "Failed to delete", {
            toastId: "category-delete-error",
          });
        }
      } catch (error) {
        errorHandler(error);
      }
    });
  };

  return (
    <>
      <Card>
        <CardContent sx={{ padding: { xs: 2, md: 4 } }}>
          <FormikProvider value={formik}>
            <Typography variant="h4" sx={{ mb: 2 }} align="start">
              {id ? "Edit" : "Add"} Sports News
            </Typography>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <Box>
                  <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="title"
                      label="Title"
                      disabled={isReadOnly}
                      {...getFieldProps("title")}
                      error={Boolean(touched.title && errors.title)}
                      helperText={touched.title && errors.title}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="flex-start" gap={1}>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.categoryId && errors.categoryId)}
                      >
                        <InputLabel id="category-label">Category</InputLabel>
                        <Select
                          labelId="category-label"
                          id="categoryId"
                          label="Category"
                          name="categoryId"
                          value={formik.values.categoryId}
                          disabled={isReadOnly}
                          onChange={(e) => setFieldValue("categoryId", e.target.value)}
                          renderValue={(selected) => {
                            if (!selected) return "";
                            const cat = categories.find((c) => c._id === selected);
                            return cat?.name || "";
                          }}
                        >
                          <MenuItem value="">
                            <em>Select Category</em>
                          </MenuItem>
                          {categories.map((cat) => (
                            <MenuItem key={cat._id} value={cat._id}>
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                width="100%"
                                gap={1}
                              >
                                <Typography component="span" noWrap sx={{ flex: 1 }}>
                                  {cat.name}
                                </Typography>
                                {!isReadOnly && (
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove ${cat.name}`}
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => handleDeleteCategory(cat, e)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        {touched.categoryId && errors.categoryId && (
                          <FormHelperText>{errors.categoryId}</FormHelperText>
                        )}
                      </FormControl>
                      {!isReadOnly && (
                        <Tooltip title="Add Category">
                          <IconButton
                            color="primary"
                            aria-label="Add Category"
                            onClick={openAddCategoryModal}
                            sx={{ mt: 0.5 }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                  </Grid>
                </Box>

                <Box>
                  <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="author"
                      label="Author"
                      disabled={isReadOnly}
                      {...getFieldProps("author")}
                      error={Boolean(touched.author && errors.author)}
                      helperText={touched.author && errors.author}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      name="tags"
                      label="Tags (Comma separated)"
                      disabled={isReadOnly}
                      {...getFieldProps("tags")}
                      error={Boolean(touched.tags && errors.tags)}
                      helperText={touched.tags && errors.tags}
                    />
                  </Grid>
                  </Grid>
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="shortDescription"
                  label="Summary"
                  disabled={isReadOnly}
                  {...getFieldProps("shortDescription")}
                  error={Boolean(touched.shortDescription && errors.shortDescription)}
                  helperText={
                    (touched.shortDescription && errors.shortDescription) ||
                    "Max 30 words"
                  }
                />

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Article</Typography>
                  <TipTapEditor
                    onChange={(html) => {
                      !isReadOnly && setFieldValue("description", html);
                    }}
                    onBlur={() => !isReadOnly && formik.setFieldTouched("description", true)}
                    data={formik.values.description}
                    placeholder="Article"
                    readOnly={isReadOnly}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <FormHelperText error sx={{ display: "block" }}>
                      {formik.errors.description}
                    </FormHelperText>
                  )}
                </Box>

                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Cover Image
                    </Typography>
                    {imagePreview ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: 140,
                          height: 140,
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Box
                          component="img"
                          src={imagePreview}
                          alt="Cover preview"
                          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/placeholder.svg";
                          }}
                        />
                        {!isReadOnly && (
                          <IconButton
                            size="small"
                            onClick={removeImage}
                            aria-label="Remove image"
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              bgcolor: "rgba(0,0,0,0.55)",
                              color: "common.white",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      !isReadOnly && (
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CloudUploadOutlinedIcon />}
                          sx={{
                            width: 140,
                            height: 140,
                            flexDirection: "column",
                            gap: 1,
                            borderStyle: "dashed",
                            textTransform: "none",
                          }}
                        >
                          Upload image
                          <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                        </Button>
                      )
                    )}
                    {imagePreview && !isReadOnly && (
                      <Button
                        variant="text"
                        component="label"
                        size="small"
                        sx={{ mt: 1, display: "block", width: "fit-content" }}
                      >
                        Change image
                        <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                      </Button>
                    )}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Trending
                    </Typography>
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Switch
                          disabled={isReadOnly}
                          checked={formik.values.featured}
                          onChange={(e) => setFieldValue("featured", e.target.checked)}
                        />
                      }
                      label={formik.values.featured ? "Yes" : "No"}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl
                      fullWidth
                      error={Boolean(formik.touched.status && formik.errors.status)}
                    >
                      <InputLabel id="status-label">Status</InputLabel>
                      <Select
                        labelId="status-label"
                        id="status"
                        label="Status"
                        value={formik.values.status}
                        disabled={isReadOnly}
                        onChange={(e) => formik.setFieldValue("status", e.target.value)}
                      >
                        <MenuItem value={1}>Active</MenuItem>
                        <MenuItem value={0}>Inactive</MenuItem>
                      </Select>
                      {formik.touched.status && formik.errors.status && (
                        <FormHelperText>{formik.errors.status}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </Stack>
              <Stack
                spacing={2}
                mt={3}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                {!isReadOnly && (
                  <Button
                    size="large"
                    type="button"
                    variant="contained"
                    disabled={isSubmitting}
                    sx={{ my: 2 }}
                    onClick={handleSubmit}
                  >
                    {id ? "Update" : "Add"}
                  </Button>
                )}

                <Button
                  size="large"
                  variant="contained"
                  onClick={() => {
                    if (!isSubmitting) navigate("/sport-news");
                  }}
                  sx={{ my: 2, ml: 4 }}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>

      <Dialog open={addCategoryOpen} onClose={closeAddCategoryModal} fullWidth maxWidth="xs">
        <DialogTitle>Add Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Category Name"
            value={newCategoryName}
            onChange={(e) => {
              setNewCategoryName(e.target.value);
              if (newCategoryError) setNewCategoryError("");
            }}
            error={Boolean(newCategoryError)}
            helperText={newCategoryError}
            disabled={addingCategory}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCategory();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddCategoryModal} disabled={addingCategory}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddCategory}
            disabled={addingCategory}
          >
            {addingCategory ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
